import { useMemo, useState } from 'react';
import { Nav, Card } from 'react-bootstrap';
import i18next from 'i18next';
import NavItem from '../../LeadsComponents/LeadNavItem';
import { dynamicTranslateOpportunityFields } from '../../../../utils/opportunity';
import { Markets, ReactSelectOption } from '../../../../types';
import _ from 'lodash';
import { useQuery } from '@tanstack/react-query';
import { LeadsWizardActiveStepEnum, RoleEnum } from '../../../../api/models/Enums';
import { useApiClient } from '../../../../hooks/useApiClient';
import SelectHmpOrExternalVehicle from '../../../VehicleSelector/SelectHmpOrExternalVehicle';
import useLeadsStore from '../../../../store/LeadsStore';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'src/hooks/useAuth';
import SendEmail from '../../../SendEmail/SendEmail';
import SfAccountsForm from '../../../Wizard/SfAccountsForm';
import SfOpportunityForm from '../../../Wizard/SfOpportunityForm';
import '../../../../pages/Leads/LeadsPage.scss';
import { isMarket } from 'src/utils/markets';

const LeadWizard = () => {
  const [validation, setValidation] = useState<{
    account: boolean;
    opportunity: boolean;
    vehicle: boolean;
  }>({ account: false, opportunity: false, vehicle: false });
  const { userReferencePartnerList, userRole } = useAuth();

  const [mustComplete, setMustComplete] = useState<LeadsWizardActiveStepEnum[]>([]);
  const { apiClient, handleApiResponse } = useApiClient();
  const { leadWizardData } = useLeadsStore();
  const { wizardActiveStep, setWizardActiveStep, setLeadWizardData } = useLeadsStore();
  const { t } = useTranslation();
  const [sendFiles, setSendFiles] = useState<File[]>([]);


  interface SendDataProps {
    enabled: boolean;
    message: string;
    to: ReactSelectOption | null;
    dealer: ReactSelectOption | null;
    dealerDocs: string[];
    cc: ReactSelectOption[];
  }

  const { data: opportunityFields } = useQuery({
    queryKey: ['fetch_opportunity_fields'],
    queryFn: async () => {
      const response = await handleApiResponse(apiClient.salesforceOpportunityController.getOpportunityFields());
      let translatedFields = dynamicTranslateOpportunityFields(response);
      translatedFields.push({
        label: t('dealer'),
        name: 'nomePartner',
        picklistValues: userReferencePartnerList.map((value) => ({
          active: true,
          label: value,
          value: value,
        })),
      });

      translatedFields = translatedFields.map(field => {
        if (field.name?.toLowerCase() === 'canale__c') {
          const hideSegnalatore = [RoleEnum.SALES_DEALER, RoleEnum.SALES_INTERNAL].includes(userRole) || isMarket(Markets.es);

          if (hideSegnalatore) {
            field.picklistValues = field.picklistValues.filter(item => item.value?.toLowerCase() !== 'segnalatore');
          }
        }
        return field;
      })

      const edisonIdx = translatedFields?.findIndex(field => field?.name === 'Offerta_Edison__c');

      if (![null, undefined, -1].includes(edisonIdx)) {
        const [edisonField] = translatedFields.splice(edisonIdx, 1);
        edisonField && isMarket(Markets.it) && translatedFields.push(edisonField);
      }

      return translatedFields;
    },
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const memoizedOpportunityFields = useMemo(() => {
    return (opportunityFields || []).filter(field => field?.name?.toLowerCase() !== 'leadsource');
  }, [opportunityFields]);

  return (
    <>
      <Nav
        fill
        variant="tabs"
        activeKey={wizardActiveStep}
        defaultActiveKey="account"
        className=""
        onSelect={(key) => {
          return
        }}
      >
        <NavItem
          eventKey="account"
          title={i18next.t('account')}
          isValid={validation.account}
          mustComplete={mustComplete}
          text={leadWizardData.account?.Name ?? i18next.t('select_one_account')}
        />

        <NavItem
          eventKey="opportunity"
          title={i18next.t('opportunity_data')}
          isValid={validation.opportunity}
          mustComplete={mustComplete}
          text={validation.opportunity ? i18next.t('completed') : i18next.t('complete_form')}
        />

        <NavItem
          eventKey="vehicle"
          title={i18next.t('vehicle')}
          isValid={validation.vehicle}
          mustComplete={mustComplete}
          showIsOptional={false}
          text={validation.vehicle ? i18next.t('completed') : i18next.t('choose_vehicle')}
        />

        <NavItem
          eventKey="send_quote"
          title={i18next.t('quotation')}
          showIsOptional={true}
          text={userRole === RoleEnum.SALES_DEALER ? i18next.t('send_upload_quote') : i18next.t('send_upload_quotation')}
        />
      </Nav>

      <Card>
        <Card.Body className="pt-4">
          {wizardActiveStep === 'account' && <SfAccountsForm />}
          {wizardActiveStep === 'opportunity' && <SfOpportunityForm opportunityFields={memoizedOpportunityFields} />}
          {wizardActiveStep === 'vehicle' && <SelectHmpOrExternalVehicle />}
          {wizardActiveStep === 'send_quote' && <SendEmail />}
        </Card.Body>
      </Card>
    </>
  );
};

export default LeadWizard;
